import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { pdf } from "@react-pdf/renderer";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { apiURL } from "../../config/config";
import PaginationControls from "../PaginationControls";
import TravelExpenseApprovalFilters from "./TravelExpenseApprovalFilters";
import TravelExpenseApprovalTable from "./TravelExpenseApprovalTable";
import TravelExpenseExportDocument from "./TravelExpenseExportDocument";
import TravelExpenseReceiptPreviewModal from "./TravelExpenseReceiptPreviewModal";
import {
  getFileType,
  getReceiptUrls,
  getTravelQuotationDisplayLabel,
  getTravelQuotationProjectLabel,
  getTravelQuotationQtNumber,
} from "./TravelExpenseApprovalUtils";

const normalizeAssetUrl = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  if (String(url).startsWith("/")) {
    return `${apiURL.replace(/\/$/, "")}${url}`;
  }

  return `${apiURL.replace(/\/$/, "")}/${String(url).replace(/^\/+/, "")}`;
};

const sanitizeFileName = (value) =>
  Array.from(String(value || ""))
    .map((char) => {
      const code = char.charCodeAt(0);
      if (code <= 31 || '<>:"/\\|?*'.includes(char)) {
        return " ";
      }

      return char;
    })
    .join("")
    .replace(/\s+/g, " ")
    .trim();

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error("Failed to read blob"));
    reader.readAsDataURL(blob);
  });

const buildFullName = (user) => {
  const firstName = String(user?.firstName || "").trim();
  const lastName = String(user?.lastName || "").trim();
  return [firstName, lastName].filter(Boolean).join(" ").trim();
};

const normalizeText = (value) => String(value || "").trim().toLowerCase();

const getExportApprovalSteps = (item) => {
  const candidates = [
    item?.approvalSteps,
    item?.approvalFlow,
    item?.approvalHierarchy?.[0]?.approvalHierarchy,
    item?.approvalHierarchy,
  ];

  return candidates.find((steps) => Array.isArray(steps)) || [];
};

const getApprovalStepLevel = (step) =>
  Number(step?.level ?? step?.approvalLevel ?? step?.approverLevel ?? 0);

const getApprovalStepStatus = (step) =>
  normalizeText(step?.status || step?.approvalStatus);

const getApprovalStepActorKey = (step) =>
  String(
    step?.actedBy ||
      step?.approvedBy ||
      step?.approver ||
      step?.email ||
      step?.username ||
      ""
  ).trim();

const getApprovalStepActedAt = (step) =>
  step?.actedAt || step?.approvedAt || step?.updatedAt || step?.actionAt || "";

const isApprovedStatus = (value) => {
  const normalized = normalizeText(value);
  return normalized === "approved" || normalized === "approve";
};

const TravelExpenseApprovalSection = ({ selectedYear, setPageLoading }) => {
  const pageSize = 10;
  const quotationCacheRef = useRef({});
  const [records, setRecords] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("Pending");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [openingReceiptUrl, setOpeningReceiptUrl] = useState("");
  const [refreshingFlowId, setRefreshingFlowId] = useState("");
  const [signedReceiptCache, setSignedReceiptCache] = useState({});
  const [modalState, setModalState] = useState({
    open: false,
    title: "",
    files: [],
    selectedIndex: 0,
  });

  const fetchTravelApprovals = useCallback(async () => {
    try {
      setPageLoading?.(true);
      const token = localStorage.getItem("token");

      const approvalsResponse = await axios.get(
        `${apiURL}travel-expenses/approvals?year=${selectedYear}&status=${status}&search=${search}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const approvalRecords = (approvalsResponse.data?.data || []).map((item) => ({
        ...item,
        quotationDisplayNumber: getTravelQuotationDisplayLabel(item),
        quotationProjectLabel: getTravelQuotationProjectLabel(item),
        quotationQtNumber: getTravelQuotationQtNumber(item),
      }));

      setRecords(approvalRecords);
    } catch (error) {
      console.error("Error fetching travel approvals:", error);
      toast.error("โหลดรายการเบิกค่าเดินทางไม่สำเร็จ");
    } finally {
      setPageLoading?.(false);
    }
  }, [search, selectedYear, setPageLoading, status]);

  const getQuotationsByYear = useCallback(async (year) => {
    const cacheKey = String(year || "");
    const cachedQuotations = quotationCacheRef.current[cacheKey];

    if (Array.isArray(cachedQuotations)) {
      return cachedQuotations;
    }

    const token = localStorage.getItem("token");
    const quotationsResponse = await axios.get(`${apiURL}quotations?year=${year}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const quotations = quotationsResponse.data || [];
    quotationCacheRef.current[cacheKey] = quotations;
    return quotations;
  }, []);

  useEffect(() => {
    fetchTravelApprovals();
  }, [fetchTravelApprovals]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, selectedYear, status]);

  useEffect(() => {
    setSelectedIds((prev) =>
      prev.filter((id) => records.some((item) => item._id === id))
    );
  }, [records]);

  const totalPages = Math.max(1, Math.ceil(records.length / pageSize));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedRecords = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return records.slice(startIndex, startIndex + pageSize);
  }, [currentPage, pageSize, records]);

  const getSignedReceiptUrl = useCallback(
    async (receiptUrl) => {
      const cachedReceipt = signedReceiptCache[receiptUrl];
      const now = Date.now();

      if (cachedReceipt?.url && cachedReceipt.expiresAt > now) {
        return cachedReceipt.url;
      }

      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${apiURL}upload/receipt/signed-url`,
        { url: receiptUrl },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const signedReceipt = response.data?.data;
      const signedUrl = signedReceipt?.url;
      const expiresIn = Number(signedReceipt?.expiresIn || 0);

      if (!signedUrl) {
        throw new Error("Signed receipt URL not found");
      }

      setSignedReceiptCache((prev) => ({
        ...prev,
        [receiptUrl]: {
          url: signedUrl,
          expiresAt: now + Math.max(expiresIn - 30, 0) * 1000,
        },
      }));

      return signedUrl;
    },
    [signedReceiptCache]
  );

  const getReceiptDataUrl = useCallback(async (url) => {
    if (!url) return "";

    const response = await fetch(url, {
      method: "GET",
      mode: "cors",
      credentials: "omit",
    });

    if (!response.ok) {
      throw new Error(`Unable to fetch receipt image: ${response.status}`);
    }

    const blob = await response.blob();
    return blobToDataUrl(blob);
  }, []);

  const handleApprove = async (id) => {
    try {
      setPageLoading?.(true);
      const token = localStorage.getItem("token");

      await axios.patch(
        `${apiURL}travel-expenses/${id}/approve`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success("อนุมัติรายการสำเร็จ");
      fetchTravelApprovals();
    } catch (error) {
      console.error("Approve error:", error);
      toast.error(error?.response?.data?.message || "อนุมัติไม่สำเร็จ");
    } finally {
      setPageLoading?.(false);
    }
  };

  const handleReject = async (id) => {
    const { value: rejectedReason } = await Swal.fire({
      title: "ไม่อนุมัติรายการ",
      text: "กรุณาระบุเหตุผลสำหรับการ reject รายการนี้",
      input: "textarea",
      inputPlaceholder:
        "เช่น เอกสารไม่ครบ, ค่าใช้จ่ายไม่ตรงตามนโยบาย",
      inputAttributes: {
        "aria-label": "กรอกเหตุผลที่ไม่อนุมัติ",
      },
      showCancelButton: true,
      confirmButtonText: "ยืนยันการ Reject",
      cancelButtonText: "ยกเลิก",
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
      reverseButtons: true,
      inputValidator: (value) => {
        if (!value?.trim()) {
          return "กรุณากรอกเหตุผลที่ไม่อนุมัติ";
        }

        return null;
      },
    });

    if (!rejectedReason?.trim()) return;

    try {
      setPageLoading?.(true);
      const token = localStorage.getItem("token");

      await axios.patch(
        `${apiURL}travel-expenses/${id}/reject`,
        { rejectedReason: rejectedReason.trim() },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success("ปฏิเสธรายการสำเร็จ");
      fetchTravelApprovals();
    } catch (error) {
      console.error("Reject error:", error);
      toast.error(error?.response?.data?.message || "ปฏิเสธไม่สำเร็จ");
    } finally {
      setPageLoading?.(false);
    }
  };

  const handleRefreshFlow = async (item) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Refresh approval flow?",
      text: "Approval progress will be reset to Pending using the requester's current flow.",
      showCancelButton: true,
      confirmButtonText: "Refresh Flow",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#2563eb",
      cancelButtonColor: "#6b7280",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      setRefreshingFlowId(item._id);
      setPageLoading?.(true);
      const token = localStorage.getItem("token");

      await axios.patch(
        `${apiURL}travel-expenses/${item._id}/update-approval-flow`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success("Approval flow refreshed successfully");
      await fetchTravelApprovals();
    } catch (error) {
      console.error("Refresh travel approval flow error:", error);
      toast.error(
        error?.response?.data?.message || "Unable to refresh approval flow"
      );
    } finally {
      setRefreshingFlowId("");
      setPageLoading?.(false);
    }
  };

  const handleDelete = async (item) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "ลบรายการนี้?",
      text: `ต้องการลบรายการของ ${item?.requestedBy || "ผู้ยื่นคำขอ"} ใช่หรือไม่`,
      showCancelButton: true,
      confirmButtonText: "ลบรายการ",
      cancelButtonText: "ยกเลิก",
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      setPageLoading?.(true);
      const token = localStorage.getItem("token");

      await axios.delete(`${apiURL}travel-expenses/${item._id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success("ลบรายการสำเร็จ");
      fetchTravelApprovals();
    } catch (error) {
      console.error("Delete travel approval error:", error);
      toast.error(error?.response?.data?.message || "ลบรายการไม่สำเร็จ");
    } finally {
      setPageLoading?.(false);
    }
  };

  const handleOpenReceiptModal = async (item) => {
    const receiptUrls = getReceiptUrls(item);
    if (receiptUrls.length === 0) return;

    try {
      setPageLoading?.(true);
      setOpeningReceiptUrl(item._id);

      const signedFiles = await Promise.all(
        receiptUrls.map(async (receiptUrl, index) => ({
          originalUrl: receiptUrl,
          signedUrl: await getSignedReceiptUrl(receiptUrl),
          type: getFileType(receiptUrl),
          label: `ไฟล์หลักฐาน ${index + 1}`,
        }))
      );

      setModalState({
        open: true,
        title: `${item.requestedBy || "ผู้ยื่นคำขอ"} · ${signedFiles.length} ไฟล์`,
        files: signedFiles,
        selectedIndex: 0,
      });
    } catch (error) {
      console.error("Error opening receipt modal:", error);
      toast.error(error?.response?.data?.message || "เปิดหลักฐานไม่สำเร็จ");
    } finally {
      setOpeningReceiptUrl("");
      setPageLoading?.(false);
    }
  };

  const openInNewTab = (url) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const selectableIds = paginatedRecords.map((item) => item._id);

  const selectedCount = selectedIds.length;
  const allSelectableChecked =
    selectableIds.length > 0 && selectedIds.length === selectableIds.length;
  const someSelectableChecked =
    selectedIds.length > 0 && selectedIds.length < selectableIds.length;

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
    );
  };

  const handleToggleSelectAll = () => {
    setSelectedIds((prev) =>
      prev.length === selectableIds.length ? [] : selectableIds
    );
  };

  const handleExportSelected = async () => {
    if (selectedIds.length === 0) {
      toast.error("กรุณาเลือกรายการที่ต้องการ export");
      return;
    }

    try {
      setPageLoading?.(true);
      setExporting(true);

      const selectedRecords = records.filter((item) =>
        selectedIds.includes(item._id)
      );
      const token = localStorage.getItem("token");

      const [usersResponse, quotations] = await Promise.all([
        axios.get(`${apiURL}users`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
        getQuotationsByYear(selectedYear),
      ]);

      const users = usersResponse.data?.users || usersResponse.data || [];

      const userNameByUsername = new Map(
        users.flatMap((user) => {
          const fullName = buildFullName(user);
          const keys = [
            String(user?.username || "").trim(),
            String(user?.email || "").trim(),
          ]
            .filter(Boolean)
            .flatMap((value) => [value, value.toLowerCase()]);

          return keys.map((key) => [key, fullName || key]);
        })
      );

      const quotationById = new Map(
        quotations.map((quotation) => [String(quotation._id), quotation])
      );

      if (selectedRecords.length > 1) {
        const quotationNumbers = Array.from(
          new Set(
            selectedRecords
              .map((item) => {
                const quotation = quotationById.get(String(item.quotationId));
                return String(
                  quotation?.quotationNumber ||
                    item?.quotationNumber ||
                    item?.quotationNo ||
                    ""
                ).trim();
              })
              .filter(Boolean)
          )
        );

        if (quotationNumbers.length > 1) {
          await Swal.fire({
            icon: "warning",
            title: "Export ไม่ได้",
            text: "เลข QT Number ไม่ตรงกัน จึงไม่สามารถ export พร้อมกันได้",
            confirmButtonText: "รับทราบ",
            confirmButtonColor: "#2563eb",
          });
          return;
        }
      }

      const recordsWithReceipts = await Promise.all(
        selectedRecords.map(async (item) => {
          const receiptUrls = getReceiptUrls(item);
          const quotation = quotationById.get(String(item.quotationId));
          const requesterName =
            buildFullName(item.requestedByUser) ||
            userNameByUsername.get(item.requestedBy) ||
            item.requestedBy ||
            "-";
          const contactCompanyName =
            quotation?.clientId?.customerName ||
            quotation?.client ||
            item.clientName ||
            item.projectName ||
            item.quotationTitle ||
            "-";
          const approvalSteps = getExportApprovalSteps(item);
          const lv3ApprovedStep = approvalSteps.find(
            (step) =>
              getApprovalStepLevel(step) === 3 &&
              isApprovedStatus(getApprovalStepStatus(step))
          );
          const lv3ActorKey = getApprovalStepActorKey(lv3ApprovedStep);
          const isFullyApproved =
            isApprovedStatus(item?.status || item?.approvalStatus) ||
            (approvalSteps.length > 0 &&
              approvalSteps.every((step) =>
                isApprovedStatus(getApprovalStepStatus(step))
              ));
          const approvedSteps = approvalSteps.filter((step) =>
            isApprovedStatus(getApprovalStepStatus(step))
          );
          const finalApprovedStep = approvedSteps.sort(
            (a, b) => getApprovalStepLevel(b) - getApprovalStepLevel(a)
          )[0];

          const signedReceiptFiles = await Promise.all(
            receiptUrls.map(async (receiptUrl, index) => {
              let signedUrl = "";
              let dataUrl = "";
              const fileType = getFileType(receiptUrl);

              try {
                signedUrl = await getSignedReceiptUrl(receiptUrl);
              } catch (error) {
                console.error("Error getting signed receipt URL for export:", error);
                signedUrl = normalizeAssetUrl(receiptUrl);
              }

              if (fileType === "image" && signedUrl) {
                try {
                  dataUrl = await getReceiptDataUrl(signedUrl);
                } catch (error) {
                  console.error("Error converting receipt image to data URL:", error);
                }
              }

              return {
                originalUrl: receiptUrl,
                signedUrl,
                dataUrl,
                type: fileType,
                label: `หลักฐาน ${index + 1}`,
              };
            })
          );

          return {
            ...item,
            requestedByFullName: requesterName,
            lv3ApproverFullName: lv3ActorKey
              ? userNameByUsername.get(lv3ActorKey) ||
                userNameByUsername.get(lv3ActorKey.toLowerCase()) ||
                lv3ActorKey
              : "",
            lv3ApprovedAt: getApprovalStepActedAt(lv3ApprovedStep),
            finalApprovedAt: isFullyApproved
              ? getApprovalStepActedAt(finalApprovedStep)
              : "",
            isFullyApproved,
            contactCompanyName,
            signedReceiptFiles,
          };
        })
      );

      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const companyName = user.company || "NEON WORKS";
      const generatedAt = new Date();

      const blob = await pdf(
        <TravelExpenseExportDocument
          records={recordsWithReceipts}
          generatedAt={generatedAt}
          companyName={companyName}
        />
      ).toBlob();

      const exportDate = generatedAt.toISOString().slice(0, 10);
      const fileNameBase = sanitizeFileName(
        selectedRecords.length === 1
          ? `travel-expense-${selectedRecords[0]?.requestedBy || "record"}-${exportDate}`
          : `travel-expense-export-${exportDate}`
      );
      const fileName = `${fileNameBase || "travel-expense-export"}.pdf`;

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);

      toast.success("สร้างและดาวน์โหลด PDF เรียบร้อย");
    } catch (error) {
      console.error("Error exporting travel expense PDF:", error);
      toast.error("ไม่สามารถ export เอกสารได้");
    } finally {
      setExporting(false);
      setPageLoading?.(false);
    }
  };

  return (
    <>
      <div>
        <TravelExpenseApprovalFilters
          search={search}
          status={status}
          onSearchChange={setSearch}
          onStatusChange={setStatus}
          onSearch={fetchTravelApprovals}
        />

        <div className="mb-4 flex flex-col gap-3 rounded-2xl border border-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 text-sm text-blue-900 shadow-sm dark:border-blue-900/40 dark:from-blue-950/30 dark:to-indigo-950/30 dark:text-blue-100">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="font-semibold">เลือกสำหรับ export หลายใบเสร็จ</div>
              <div className="text-xs text-blue-700 dark:text-blue-300">
                เลือกรายการเพื่อ export เอกสารได้พร้อมกันหลายใบ
              </div>
            </div>
            <div className="inline-flex items-center gap-2">
              <span className="rounded-full bg-white/80 px-3 py-1 font-semibold text-blue-800 dark:bg-blue-900/40 dark:text-blue-100">
                เลือกแล้ว {selectedCount} รายการ
              </span>
              <button
                type="button"
                onClick={handleExportSelected}
                disabled={selectedCount === 0 || exporting}
                className={`rounded-full px-3 py-1 font-semibold text-white transition ${
                  selectedCount === 0 || exporting
                    ? "cursor-not-allowed bg-blue-300 dark:bg-blue-800/50"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {exporting ? "กำลังสร้าง PDF..." : "Export Selected"}
              </button>
              {selectedCount > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedIds([])}
                  className="rounded-full border border-blue-200 px-3 py-1 font-medium text-blue-700 transition hover:bg-blue-100 dark:border-blue-800 dark:text-blue-200 dark:hover:bg-blue-900/40"
                >
                  ล้างการเลือก
                </button>
              )}
            </div>
          </div>
        </div>

        <TravelExpenseApprovalTable
          records={paginatedRecords}
          selectedIds={selectedIds}
          openingReceiptUrl={openingReceiptUrl}
          refreshingFlowId={refreshingFlowId}
          allSelectableChecked={allSelectableChecked}
          someSelectableChecked={someSelectableChecked}
          onToggleSelectAll={handleToggleSelectAll}
          onToggleSelect={handleToggleSelect}
          onOpenReceiptModal={handleOpenReceiptModal}
          onApprove={handleApprove}
          onReject={handleReject}
          onRefreshFlow={handleRefreshFlow}
          onDelete={handleDelete}
        />

        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>

      <TravelExpenseReceiptPreviewModal
        modalState={modalState}
        setModalState={setModalState}
        onOpenInNewTab={openInNewTab}
      />
    </>
  );
};

export default TravelExpenseApprovalSection;
